import { useTranslation } from 'react-i18next';
import { FALLBACK_USAGE_LIMITS, USAGE_FEATURES } from '../constants/UsageLimits';
import { TIER_FEATURES } from '../constants/TierFeatures';
import { useUser } from '../context/UserContext';

export const useTierFeatures = () => {
  const { t } = useTranslation();
  const { globalTierLimits } = useUser();

  const getTierFeatures = (tier = 'free') => {
    // NOTE: For paid tier plan descriptions (advanced, elite) on the Paywall,
    // we use FALLBACK_USAGE_LIMITS instead of globalTierLimits from the API.
    // This ensures the displayed values match the marketing/pricing table.
    // TODO: Update backend tier limits to match, then remove this override.
    const isPaidTier = tier === 'advanced' || tier === 'elite';
    const limitsSource = isPaidTier ? FALLBACK_USAGE_LIMITS : (globalTierLimits || FALLBACK_USAGE_LIMITS);
    const rawTierLimits = limitsSource[tier] || limitsSource.free;
    const tierFeatures = TIER_FEATURES[tier] || TIER_FEATURES.free;

    // Helper to handle -1 as Infinity and fallback to 0
    const getLimitValue = (val) => (val === -1 ? Infinity : (val ?? 0));

    const tierLimits = {
      [USAGE_FEATURES.BIQ_QUERY]: getLimitValue(rawTierLimits[USAGE_FEATURES.BIQ_QUERY]),
      [USAGE_FEATURES.BIQ_DEEP_THINKING]: getLimitValue(rawTierLimits[USAGE_FEATURES.BIQ_DEEP_THINKING]),
      [USAGE_FEATURES.ANALYTICS_VIEW]: getLimitValue(rawTierLimits[USAGE_FEATURES.ANALYTICS_VIEW]),
      [USAGE_FEATURES.DEFAULT_ALERT_CREATE]: getLimitValue(rawTierLimits[USAGE_FEATURES.DEFAULT_ALERT_CREATE]),
      [USAGE_FEATURES.CUSTOM_ALERT_CREATE]: getLimitValue(rawTierLimits[USAGE_FEATURES.CUSTOM_ALERT_CREATE]),
      [USAGE_FEATURES.REPORT_GENERATION]: getLimitValue(rawTierLimits[USAGE_FEATURES.REPORT_GENERATION]),
    };

    const formatLimit = (limit) => {
      if (limit === Infinity) {
        return t('subscription.unlimited');
      }
      return limit.toLocaleString();
    };

    const featuresList = [];

    // For paid tiers (advanced, elite), use the new feature format matching the plans image
    if (tier === 'advanced' || tier === 'elite') {
      // Mobile App Access (all plans)
      featuresList.push({ text: t('subscription.features.mobileAppAccess') });

      // Desktop WebApp Access (all plans)
      featuresList.push({ text: t('subscription.features.webappAccess') });

      // Monthly Agentic AI BiQ™ Queries (moved to top after Desktop Access)
      featuresList.push({
        text: t('subscription.features.biqQueries', {
          count: formatLimit(tierLimits[USAGE_FEATURES.BIQ_QUERY]),
        })
      });

      // Monthly Deep Thinking AI BiQ™ Queries (moved to top after BiQ Queries, with "New" badge)
      featuresList.push({
        text: t('subscription.features.deepThinkingQueries', {
          count: formatLimit(tierLimits[USAGE_FEATURES.BIQ_DEEP_THINKING]),
        }),
        isNew: true,
      });

      // Monthly Market Intelligence PDF Reports
      featuresList.push({
        text: t('subscription.features.pdfReports', {
          count: formatLimit(tierLimits[USAGE_FEATURES.REPORT_GENERATION]),
        })
      });

      // Smart Alerts
      featuresList.push({
        text: t('subscription.features.defaultAlerts', {
          count: formatLimit(tierLimits[USAGE_FEATURES.DEFAULT_ALERT_CREATE])
        }),
        hasTooltip: true,
        tooltipText: t('subscription.features.smartAlertsTooltip')
      });

      // Custom Alerts
      featuresList.push({
        text: t('subscription.features.customAlerts', {
          count: formatLimit(tierLimits[USAGE_FEATURES.CUSTOM_ALERT_CREATE])
        })
      });

      // WhatsApp Integration (all plans)
      featuresList.push({ text: t('subscription.features.whatsappIntegration') });

      // One-Click Market Trends (all plans)
      featuresList.push({ text: t('subscription.features.oneClickMarketTrends') });

      // Analytics: Price Pulse (advanced and elite only)
      if (tier === 'advanced' || tier === 'elite') {
        featuresList.push({ text: t('subscription.features.analyticsPricePulse') });
      }

      // Analytics: Smart Price Tracker (all plans)
      featuresList.push({ text: t('subscription.features.analyticsSmartPriceTracker') });

      // Analytics: Volume by Origin (all plans)
      featuresList.push({ text: t('subscription.features.analyticsVolumeByOrigin') });

      // Analytics: Commodity Planner (advanced and elite only)
      if (tier === 'advanced' || tier === 'elite') {
        featuresList.push({ text: t('subscription.features.analyticsCommodityPlanner') });
      }

      // Analytics: Crop Caster (elite only)
      if (tier === 'elite') {
        featuresList.push({ text: t('subscription.features.analyticsCropCaster') });
      }

      // Multiple Users (elite only)
      if (tier === 'elite') {
        featuresList.push({ text: t('subscription.features.multipleUsers') });
      }

      // Personalized Configuration (elite only)
      if (tier === 'elite') {
        featuresList.push({ text: t('subscription.features.personalizedConfiguration') });
      }
    } else {
      // Fallback for free/ifpa tiers - use old logic
      if (tierFeatures.includes('basic_chat')) {
        const biqQueries = tierLimits[USAGE_FEATURES.BIQ_QUERY];
        if (biqQueries !== 0) {
          featuresList.push(
            t('subscription.features.normalQuestions', {
              count: formatLimit(biqQueries),
              period: t('subscription.periods.perMonth')
            })
          );
        }
      }

      if (tierFeatures.includes('basic_analytics')) {
        const analyticsViews = tierLimits[USAGE_FEATURES.ANALYTICS_VIEW];
        if (analyticsViews !== 0) {
          featuresList.push(
            t('subscription.features.analyticsViews', {
              count: formatLimit(analyticsViews),
              period: t('subscription.periods.perMonth')
            })
          );
        }
      }

      // Smart Alerts
      if (tierFeatures.includes('default_alerts')) {
        const defaultAlerts = tierLimits[USAGE_FEATURES.DEFAULT_ALERT_CREATE];
        if (defaultAlerts !== 0) {
          featuresList.push({
            text: t('subscription.features.defaultAlerts', {
              count: formatLimit(defaultAlerts)
            }),
            hasTooltip: true,
            tooltipText: t('subscription.features.smartAlertsTooltip')
          });
        }
      }

      if (tierFeatures.includes('custom_alerts')) {
        const customAlerts = tierLimits[USAGE_FEATURES.CUSTOM_ALERT_CREATE];
        if (customAlerts !== 0) {
          featuresList.push(
            t('subscription.features.customAlerts', {
              count: formatLimit(customAlerts)
            })
          );
        }
      }

      const pdfReports = tierLimits[USAGE_FEATURES.REPORT_GENERATION];
      if (pdfReports !== 0) {
        featuresList.push(
          t('subscription.features.pdfReports', {
            count: formatLimit(pdfReports),
            period: t('subscription.periods.perMonth')
          })
        );
      }

      if (tierFeatures.includes('webapp_access')) {
        featuresList.push(t('subscription.features.webappAccess'));
      }

      if (tierFeatures.includes('whatsapp_access')) {
        featuresList.push(t('subscription.features.whatsappSupport'));
      }
    }

    return featuresList;
  };

  const getPlanMetadata = (planId) => {
    const planPrices = {
      advanced: { period: t('subscription.periods.month'), highlighted: true },
      elite: { period: t('subscription.periods.month'), highlighted: false },
    };

    const metadata = planPrices[planId];
    if (!metadata) return null;

    return {
      ...metadata,
      features: getTierFeatures(planId),
    };
  };

  return {
    getTierFeatures,
    getPlanMetadata,
    limits: globalTierLimits || FALLBACK_USAGE_LIMITS,
    USAGE_FEATURES,
  };
};
